<script setup lang="ts">
  defineOptions({ name: 'ExhibitorCenterTopBarToggle' })

  import { computed } from 'vue'

  import { useRoute } from 'nuxt/app'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import { resolveCpBreadcrumbRegistryKey } from '@common/navigation-layer/config/cp-breadcrumb-registry'

  import CPTabPane from '@ui/components/CPTabPane.vue'

  import CPTabs from '@ui/components/CPTabs.vue'

  import { useExhibitorAdminContext } from '../../composables/useExhibitorAdminContext'

  const { t } = useI18n()
  const route = useRoute()
  const { goToCenter, goToEvent } = useExhibitorAdminContext()

  const isOnCenter = computed(() => resolveCpBreadcrumbRegistryKey(route) === 'clientportal-exhibitor-center')
  const activeTab = computed(() => (isOnCenter.value ? 'exhibitor-center' : 'event'))

  const selectTab = (value: string | number) => {
    if (value === activeTab.value) return
    void (value === 'exhibitor-center' ? goToCenter() : goToEvent())
  }
</script>

<template>
  <ClientOnly>
    <CPTabs
      :id="createElementId('exhibitor-center', 'topbar', 'toggle')"
      class="exhibitor-center-topbar-toggle"
      type="segment"
      theme="secondary"
      size="sm"
      :value="activeTab"
      :aria-label="t('clientportal.events.detail.exhibitorCenterToggleAria')"
      @update:value="selectTab"
    >
      <CPTabPane
        name="event"
        tab-key="event"
        :tab="t('clientportal.events.detail.exhibitorCenterToggleEvent')"
        :tab-props="{ id: createElementId('exhibitor-center', 'topbar', 'toggle-event') }"
      />
      <CPTabPane
        name="exhibitor-center"
        tab-key="exhibitor-center"
        :tab="t('clientportal.events.detail.exhibitorCenterToggleCenter')"
        :tab-props="{ id: createElementId('exhibitor-center', 'topbar', 'toggle-center') }"
      />
    </CPTabs>
  </ClientOnly>
</template>

<style scoped>
  :deep(.hr-tabs-pane-wrapper) {
    display: none !important;
  }
</style>

<style>
  /*
   * Unscoped on purpose, like EventsListSection: CPTabs renders the toggle through HLTabs, so a scoped
   * selector would not reach its nav. The unique class keeps this from leaking. Light mode only puts
   * back the HighRise `secondary` segment look that CPTabs' token bridge replaced: a grey rail, a white
   * capsule under the active tab, neutral text, and the theme's own hover. Dark mode keeps CPTabs.
   */
  .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-tab-wrapper {
    width: 8rem !important;
  }

  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs {
    --n-color: transparent !important;
    --n-color-segment: rgb(247, 247, 250) !important;
    --n-tab-color-segment: var(--base-white) !important;
    --n-tab-text-color: var(--cp-font-600) !important;
    --n-tab-text-color-active: var(--cp-font-900) !important;
    --n-tab-text-color-hover: var(--cp-font-800) !important;
    --n-tab-text-color-disabled: var(--cp-font-400) !important;
    --n-tab-border-color: var(--cp-border-200) !important;
    background-color: transparent !important;
  }

  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-nav,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-nav,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-nav-scroll-wrapper,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-nav-scroll-wrapper,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-content,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-content {
    background-color: transparent !important;
  }

  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-tab:not(.hr-tabs-tab--disabled):hover,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-tab:not(.n-tabs-tab--disabled):hover {
    background-color: var(--n-tab-hover-bg-color) !important;
  }

  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-tab:focus,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-tab:focus,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .hr-tabs-tab:focus-visible,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs .n-tabs-tab:focus-visible,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs.hr-tabs--segment-type .hr-tabs-tab--active,
  html:not([data-theme='dark']) .exhibitor-center-topbar-toggle.cp-tabs.n-tabs--segment-type .n-tabs-tab--active {
    background-color: transparent !important;
  }
</style>
